import React from "react";
import { useTranslation } from "react-i18next";
import { Cart } from "../../types/PricingCalculator";
import { softwarePricing } from "../../data/PricingData";

interface EligibilityStepProps {
  cart: Cart;
  onUpdate: (updates: Partial<Cart>) => void;
}

const EligibilityStep: React.FC<EligibilityStepProps> = ({
  cart,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const handleEligibilityChoice = (isEligible: boolean) => {
    onUpdate({
      eligibility: {
        rrdbRegistrationDateISO: isEligible ? "eligible" : "not-eligible",
        isFree: isEligible,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-300 mb-2">
          {t("pricingCalc.eligibilityStep.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t("pricingCalc.eligibilityStep.question")}
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              {t("pricingCalc.eligibilityStep.cardTitle")}
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                {t("pricingCalc.eligibilityStep.cardInfoPrefix")}{" "}
                <span className="font-semibold">
                  {softwarePricing.capRWF.toLocaleString()} RWF
                </span>{" "}
                {t("pricingCalc.eligibilityStep.cardInfoSuffix")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => handleEligibilityChoice(true)}
            className={`p-6 border-2 rounded-lg text-left transition-colors ${
              cart.eligibility.isFree === true
                ? "border-green-500 bg-green-50 dark:bg-slate-900"
                : "border-gray-300 hover:border-gray-400"
            }`}
          >
            <div className="flex items-center mb-2">
              <input
                type="radio"
                checked={cart.eligibility.isFree === true}
                onChange={() => handleEligibilityChoice(true)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
              />
              <span className="ml-3 text-lg font-medium">
                {t("pricingCalc.eligibilityStep.yesLabel")}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("pricingCalc.eligibilityStep.yesDesc")}
            </p>
          </button>

          <button
            onClick={() => handleEligibilityChoice(false)}
            className={`p-6 border-2 rounded-lg text-left transition-colors ${
              cart.eligibility.isFree === false &&
              cart.eligibility.rrdbRegistrationDateISO !== ""
                ? "border-red-500 bg-red-50 dark:bg-slate-900"
                : "border-gray-300 hover:border-gray-400"
            }`}
          >
            <div className="flex items-center mb-2">
              <input
                type="radio"
                checked={
                  cart.eligibility.isFree === false &&
                  cart.eligibility.rrdbRegistrationDateISO !== ""
                }
                onChange={() => handleEligibilityChoice(false)}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
              />
              <span className="ml-3 text-lg font-medium">
                {t("pricingCalc.eligibilityStep.noLabel")}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("pricingCalc.eligibilityStep.noDesc")}
            </p>
          </button>
        </div>
      </div>

      {cart.eligibility.rrdbRegistrationDateISO && (
        <div
          className={`p-4 rounded-lg ${
            cart.eligibility.isFree
              ? "bg-green-50 border border-green-200"
              : "bg-yellow-50 border border-yellow-200"
          }`}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              {cart.eligibility.isFree ? (
                <svg
                  className="h-5 w-5 text-green-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5 text-yellow-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.726-1.36 3.491 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3
                className={`text-sm font-medium ${
                  cart.eligibility.isFree ? "text-green-800" : "text-yellow-800"
                }`}
              >
                {cart.eligibility.isFree
                  ? t("pricingCalc.eligibilityStep.congratsTitle")
                  : t("pricingCalc.eligibilityStep.planRequiredTitle")}
              </h3>
              <div
                className={`mt-1 text-sm ${
                  cart.eligibility.isFree ? "text-green-700" : "text-yellow-700"
                }`}
              >
                {cart.eligibility.isFree
                  ? t("pricingCalc.eligibilityStep.congratsMsg")
                  : t("pricingCalc.eligibilityStep.planRequiredMsg")}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EligibilityStep;
